import { useTranslation } from 'react-i18next';

import CustomModal from '@components/CustomModal';
import { Input, Text, Button } from '@fragments';
import { IconBolt, IconKey, IconLink, IconLoader2, IconPlus, IconPuzzle } from '@tabler/icons-react';

import { generateDescription } from '../enums/actionTypes';
import ActionCard from './components/ActionCard';
import { PLATFORMS } from '../../../enums/platforms';
import PlatformLogo from '../../../components/PlatformLogo';
import './styles.scss';



const PLATFORM_OPTIONS = [
    {
        value: 'pluglead',
        label: PLATFORMS.pluglead.name,
        icon: PLATFORMS.pluglead.logo ? <PlatformLogo platformKey="pluglead" style={{ marginLeft: '-8px' }} /> : null
    },
    {
        value: 'activecampaign',
        label: PLATFORMS.activecampaign.name,
        icon: PLATFORMS.activecampaign.logo ? <PlatformLogo platformKey="activecampaign" style={{ marginLeft: '-8px' }} /> : null,
    },
    {
        value: 'webhook',
        label: PLATFORMS.webhook.name,
        icon: <PlatformLogo platformKey="webhook" style={{ marginLeft: '-8px' }} />,
    },
];

// Built from the caller's `t` so the options follow the active language.
const getEventOptions = (t) => [
    { value: 'payments.confirmed', label: t('manage.eventOptions.confirmed') },
    { value: 'payments.pix_generated', label: t('manage.eventOptions.pixGenerated'), disabled: true },
    { value: 'payments.checkout_dropped', label: t('manage.eventOptions.checkoutDropped'), disabled: true },
];

function StepIndicator({ step }) {
    const { t } = useTranslation('integrations');
    const steps = [t('manage.steps.general'), t('manage.steps.actions')];
    return (
        <div className="manage-integration-modal__steps pb-1 pt-1">
            {steps.map((label, idx) => (
                <div key={idx} className="manage-integration-modal__steps-item">
                    {idx > 0 && <div className="manage-integration-modal__steps-connector" />}
                    <span className={`manage-integration-modal__steps-dot${idx === step ? ' manage-integration-modal__steps-dot--active' : idx < step ? ' manage-integration-modal__steps-dot--done' : ''}`}>
                        {idx + 1}
                    </span>
                    <Text.span contrast={idx === step ? 8 : 5}>
                        {label}
                    </Text.span>
                </div>
            ))}
        </div>
    );
}

function IntegrationForm({ integration, updateIntegration, community, isEdit }) {
    const { t } = useTranslation('integrations');

    const planOptions = (community?.plans ?? []).map(p => ({
        value: String(p.id),
        label: p.label,
    }));

    const description = generateDescription(
        t,
        integration.event,
        integration.plan_id,
        integration.platform,
        community,
    );

    return (
        <div className="manage-integration-modal__form">
            <Input.Select
                label={t('manage.form.platformLabel')}
                placeholder={t('manage.form.platformPlaceholder')}
                value={integration.platform}
                onInput={v => updateIntegration({ platform: v })}
                options={PLATFORM_OPTIONS}
                disabled={isEdit}
            />
            <Input.Select
                label={t('common:entities.plan')}
                placeholder={t('manage.form.planPlaceholder')}
                value={String(integration.plan_id ?? '')}
                onInput={v => updateIntegration({ plan_id: v })}
                options={planOptions}
                disabled={isEdit}
            />
            <Input.Select
                label={t('manage.form.eventLabel')}
                placeholder={t('manage.form.eventPlaceholder')}
                value={integration.event}
                onInput={v => updateIntegration({ event: v })}
                options={getEventOptions(t)}
                disabled={isEdit}
            />
            <Input.Text
                label={t('manage.form.nameLabel')}
                placeholder={t('manage.form.namePlaceholder')}
                value={integration.name}
                onInput={v => updateIntegration({ name: v })}
                size={50}
            />
            {integration.platform === 'activecampaign' && (
                <>
                    <Input.Text
                        label={t('manage.form.apiKeyLabel')}
                        placeholder={t('manage.form.apiKeyPlaceholder')}
                        value={integration.apiKey ?? ''}
                        onInput={v => updateIntegration({ apiKey: v })}
                        start={<IconKey size={16} />}
                        disabled={isEdit}
                    />
                    <Input.Text
                        label={t('manage.form.endpointLabel')}
                        placeholder={t('manage.form.endpointPlaceholder')}
                        value={integration.endpoint ?? ''}
                        onInput={v => updateIntegration({ endpoint: v })}
                        start={<IconLink size={16} />}
                        disabled={isEdit}
                    />
                </>
            )}
            {description && (
                <Text.span contrast={6}>
                    {description}
                </Text.span>
            )}
        </div>
    );
}

function ActionsSection({ actions, integration, handleAddAction, handleUpdateAction, handleRemoveAction, handleTestAction, testStates, isCreate }) {
    const { t } = useTranslation('integrations');
    const sectionTitle = isCreate
        ? t('manage.actionsSection.titleCreate')
        : t('manage.actionsSection.title');

    return (
        <>
            <div className="manage-integration-modal__actions-header">
                <Text.h4 weight={400} contrast={8} left={<IconBolt />}>{sectionTitle}</Text.h4>
                <Button
                    startIcon={<IconPlus size={16} />}
                    onClick={handleAddAction}
                    color="overlay"
                    variant="dashed"
                >
                    {t('manage.actionsSection.add')}
                </Button>
            </div>

            {actions.length === 0 ? (
                <div className="manage-integration-modal__actions-empty">
                    <Text.span contrast={6} left={<IconPuzzle size={16} />}>
                        {t('manage.actionsSection.empty')}
                    </Text.span>
                </div>
            ) : (
                <div className="manage-integration-modal__actions-list">
                    {actions.map((action, idx) => (
                        <ActionCard
                            key={action._key}
                            action={action}
                            index={idx}
                            platform={integration.platform}
                            integrationId={integration.id}
                            onUpdate={patch => handleUpdateAction(action._key, patch)}
                            onRemove={() => handleRemoveAction(action._key)}
                            onTest={() => handleTestAction(action._key)}
                            testState={testStates?.[action._key]}
                        />
                    ))}
                </div>
            )}
        </>
    );
}

export default function ManageIntegrationView({
    open,
    loading,
    submitting,
    error,
    mode,
    step,
    integration,
    actions,
    updateIntegration,
    handleAddAction,
    handleUpdateAction,
    handleRemoveAction,
    handleTestAction,
    testStates,
    handleToggle,
    handleSubmit,
    handleSecondary,
    handleClose,
    submitLabel,
    cancelLabel,
    title,
    community,
}) {
    const { t } = useTranslation('integrations');
    const isCreate = mode === 'create';
    const isEdit = mode === 'edit';

    const activeToggle = !isCreate && (
        <div className="manage-integration-modal__active-toggle">
            <Text.span contrast={5} style={{ fontSize: 13 }}>
                {integration.active ? t('manage.active') : t('manage.inactive')}
            </Text.span>
            <Input.Switch
                value={integration.active}
                onInput={handleToggle}
            />
        </div>
    );

    const bodyContent = loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--gap-4)' }}>
            <IconLoader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
    ) : (
        <div style={{ padding: 'var(--gap-2) var(--gap-3)' }}>
            {/* Step indicator — create mode only */}
            {isCreate && <StepIndicator step={step} />}

            {/* Step 0 (create) or top section (edit) */}
            {(isCreate ? step === 0 : true) && (
                <IntegrationForm
                    integration={integration}
                    updateIntegration={updateIntegration}
                    community={community?.current ?? community}
                    isEdit={isEdit}
                />
            )}

            {/* Edit mode: divider between form and actions */}
            {isEdit && <div className="manage-integration-modal__divider" />}

            {/* Step 1 (create) or bottom section (edit) */}
            {(isCreate ? step === 1 : true) && (
                <ActionsSection
                    actions={actions}
                    integration={integration}
                    handleAddAction={handleAddAction}
                    handleUpdateAction={handleUpdateAction}
                    handleRemoveAction={handleRemoveAction}
                    handleTestAction={handleTestAction}
                    testStates={testStates}
                    isCreate={isCreate}
                />
            )}

            {/* Error */}
            {error && (
                <div className="manage-integration-modal__error">
                    {error}
                </div>
            )}
        </div>
    );

    return (
        <CustomModal
            open={open}
            maxWidth={500}
            title={title}
            topActions={activeToggle}
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            handleSecondary={handleSecondary}
            bottomButtonSumbitText={submitLabel}
            bottomButtonCancelText={cancelLabel}
            loading={submitting}
            bodyComponent={open ? bodyContent : null}
        />
    );
}
